import type { LocationQuery, RouteLocationNormalizedLoadedGeneric } from 'vue-router';
import { keepPreviousData } from '@tanstack/vue-query';
import { getCriteriaFromQuery, getUrlParamsFromCriteria } from '#shared/property/url-serp';
import type { NuxtRouteParamValue } from '#shared/application';
import type { PropertyOnCard } from '#shared/property/types';
import type { PropertyOnPage } from '#shared/property/api-types';
import type { PropertySearchCriteria, PropertySearchCriteriaName, PartialPropertySearchCriteria, PropertySearchCriteriaNameList } from '#shared/property/search-criteria';
import type { ContactForm, ContactFormBody } from '#shared/contact/types';
import type { IadAppRoute } from '~/application';

export function useSearchProperties(serpSlug: Ref<NuxtRouteParamValue>, searchParams: Ref<LocationQuery>) {
  const { locale } = useI18n();
  return useQuery({
    queryKey: [`properties`, serpSlug, searchParams],
    placeholderData: keepPreviousData,
    queryFn: () => {
      // remove serpSlug that might come from search params
      const { serpSlug: _serpSlug, ...safeSearchParams } = searchParams.value;
      const params = { serpSlug: serpSlug.value, ...safeSearchParams };
      return $fetch(`/api/properties`, {
        retry: 0,
        method: 'GET',
        params,
        query: {
          locale: locale.value,
        },
      });
    },
    retry: false,
  });
}

export function useFetchProperty(propertyListingRef: MaybeRef<string | string[] | undefined>) {
  const { locale } = useI18n();
  const propertyListingRefReactive = toRef(propertyListingRef);

  return useQuery({
    queryKey: [`properties`, propertyListingRefReactive],
    queryFn: () => {
      return $fetch(`/api/properties/${propertyListingRefReactive.value}`, {
        retry: 0,
        method: 'GET',
        query: {
          locale: locale.value,
        },
      });
    },
    retry: false,
  });
}

export const usePropertyTitle = (property: Ref<PropertyOnCard | undefined>) => {
  const { t } = useI18n();
  const title = computed(() => {
    if (!property.value) return ``;
    return t(`property.common.title`, {
      type: t(`property.types_and_sub_types.${property.value.propertyDisplayType}`),
      city: property.value.location.place,
      zip: property.value.location.postcode,
    });
  });

  return { title };
};

export const usePropertyMetaTitle = (property: Ref<PropertyOnPage | undefined>) => {
  const { t } = useI18n();
  const roomsCount = property.value?.roomsCount ?? 0;

  const title = computed(() => {
    if (!property.value) return ``;
    const seoKey = `property.meta.title_${property.value.propertyType}`;
    return t(seoKey, {
      subtype: t(`property.types_and_sub_types.${property.value.propertyDisplayType}`),
      count: roomsCount,
      surface: property.value.surface,
      place: property.value.location.place,
      postcode: property.value.location.postcode,
    });
  });
  const pageTitle = computed(() => {
    if (!property.value) return ``;
    const seoKey = `property.page_meta.title_${property.value.transactionType}_${property.value.propertyType}`;
    return t(seoKey, {
      // force display type to lowercase for french
      subtype: t(`property.types_and_sub_types.${property.value.propertyDisplayType}`).toLowerCase(),
      place: property.value.location.place,
      postcode: property.value.location.postcode,
      count: roomsCount,
      surface: property.value.surface ? t('core.common.surface', { surface: property.value.surface }) : '',
      propertyListingRef: property.value.propertyListingRef,
    });
  });
  return { title, pageTitle };
};

export const usePropertySearchCriteria = (route: RouteLocationNormalizedLoadedGeneric) => {
  const propertySearchCriteria = computed(() => {
    return getCriteriaFromQuery({
      serpSlug: route.params.serpSlug,
      location: route.query.location,
      radius: route.query.radius,
      propertyType: route.query.propertyType,
      priceMin: route.query.priceMin,
      priceMax: route.query.priceMax,
      surfaceMin: route.query.surfaceMin,
      surfaceMax: route.query.surfaceMax,
      feature: route.query.feature,
      room: route.query.room,
      epc: route.query.epc,
      bedroom: route.query.bedroom,
      sort: route.query.sort,
      groundSurfaceMin: route.query.groundSurfaceMin,
      groundSurfaceMax: route.query.groundSurfaceMax,
      projectType: route.query.projectType,
      page: route.query.page,
    });
  });

  return { propertySearchCriteria };
};

const COUNT_EXCLUDED_SEARCH_CRITERIA: PropertySearchCriteriaNameList = [
  'page',
  'itemsPerPage',
  'sort',
  'excludeRefs',
  'transactionType',
];
const COUNT_EXCLUDED_SECONDARY_SEARCH_CRITERIA: PropertySearchCriteriaNameList = [
  ...COUNT_EXCLUDED_SEARCH_CRITERIA,
  'locations',
  'radius',
  'propertyTypes',
  'priceMin',
  'priceMax',
  'surfaceMin',
  'surfaceMax',
  'rooms',
];

export function usePropertySearchCriteriaCount(propertySearchCriteria: Ref<PropertySearchCriteria>, customIncludeCriteria?: PropertySearchCriteriaNameList) {
  const filtersSummary = computed(() => countFilters(COUNT_EXCLUDED_SEARCH_CRITERIA));
  const filtersCount = toRef(() => filtersSummary.value.count);
  const filtersKeys = toRef(() => filtersSummary.value.keys);

  const secondaryFiltersCount = computed<number>(() => {
    return countFilters(COUNT_EXCLUDED_SECONDARY_SEARCH_CRITERIA).count;
  });

  const filtersIncludeSummary = computed(() => {
    return countFilters(customIncludeCriteria ?? [], `include`);
  });

  interface ActiveCriteriaSummary {
    count: number
    keys: Array<PropertySearchCriteriaName>
  }

  function countFilters(criteria: Array<PropertySearchCriteriaName>, mode: `include` | `exclude` = `exclude`): ActiveCriteriaSummary {
    const isIncludeMode = mode === `include`;
    const handledParameters = Object
      .entries(propertySearchCriteria.value)
      .filter(([key]) => criteria.includes(key as PropertySearchCriteriaName) === isIncludeMode);

    const criteriaSummary = handledParameters
      .reduce<ActiveCriteriaSummary>((acc, [key, value]) => {
        if (!value) return acc;
        const countUpdate = Array.isArray(value) ? value.length : 1;
        if (!countUpdate) return acc;
        acc.count = acc.count + countUpdate;
        acc.keys.push(key as PropertySearchCriteriaName);
        return acc;
      }, { count: 0, keys: [] });

    return criteriaSummary;
  }

  return {
    filtersCount,
    secondaryFiltersCount,
    filtersKeys,
    filtersIncludeSummary,
  };
}

export function useSerpLinkFromCriteria(partialPropertySearchCriteria: PartialPropertySearchCriteria) {
  const { locale } = useI18n();

  return computed<IadAppRoute<`serp-serpSlug`>>(() => {
    const params = getUrlParamsFromCriteria(locale.value, partialPropertySearchCriteria);
    return {
      name: 'serp-serpSlug',
      params: { serpSlug: params.serpSlug },
      query: params.query,
    };
  });
}

export function usePropertyContactForm(propertyListingRef: Ref<string>) {
  return useMutation({
    mutationFn: (contactForm: ContactForm) => {
      const body: ContactFormBody = {
        contactForm,
        marketingTracking: getMarketingTracking(),
      };
      return $fetch(`/api/properties/${propertyListingRef.value}`, {
        body,
        method: 'POST',
      });
    },
  });
}
